"use client";

import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../../components/ui/chart";
import { Select, SelectTrigger, SelectValue, SelectItem, SelectContent } from "../../components/ui/select";

export function TopGamesBarChart() {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(7);
  const [brandNames, setBrandNames] = useState([]);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);

        // 1) Fetch raw data
        const response = await fetch(`${process.env.REACT_APP_CMC_API}/statistics?method=getTotalPlayersByGame&days=${selectedDays}`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const rawData = await response.json();

        // 2) Transform raw arrays into objects with all data
        const data = rawData.game_ID.map((id, index) => ({
          gameId: id,
          name: rawData.name[index],
          version: rawData.version[index],
          totalPlayers: rawData.totalPlayers[index],
        }));

        // 3) Dynamically generate company groups (group by game name)
        const companyGroups = {};
        data.forEach(game => {
          if (!companyGroups[game.name]) {
            companyGroups[game.name] = [];
          }
          companyGroups[game.name].push(game.gameId);
        });

        // 4) Dynamically generate brand mappings (group by version)
        const companyGames = {};
        data.forEach(game => {
          if (!companyGames[game.version]) {
            companyGames[game.version] = [];
          }
          if (!companyGames[game.version].includes(game.gameId)) {
            companyGames[game.version].push(game.gameId);
          }
        });

        // 5) Update brand names for rendering
        setBrandNames(Object.keys(companyGames));

        // 6) Build chart data, one row per "company" from companyGroups
        const groupedData = [];
        for (const [company, gameIds] of Object.entries(companyGroups)) {
          const companyData = {
            company, // For the Y-axis label
          };

          let companyTotal = 0;

          // 7) For each game in the company, find its brand
          gameIds.forEach((gameId) => {
            // Find how many players
            const gameRecord = data.find((d) => d.gameId === gameId);
            const players = gameRecord ? Number(gameRecord.totalPlayers) : 0;
            companyTotal += players;

            // Find which brand the game belongs to
            const brandName = Object.entries(companyGames).find(([_, brandGameIds]) => brandGameIds.includes(gameId))?.[0] || "Unknown Brand";

            // Add the players to the brand's total for this "company" row
            if (companyData[brandName] == null) {
              companyData[brandName] = 0;
            }
            companyData[brandName] = companyData[brandName] + players;
          });

          companyData.totalPlayers = companyTotal;
          groupedData.push(companyData);
        }

        // 8) Sort by total players (descending)
        groupedData.sort((a, b) => b.totalPlayers - a.totalPlayers);

        // 9) Filter out companies with 0 players
        const filteredData = groupedData.filter(company => company.totalPlayers > 500);

        // 10) Store final array in state
        setChartData(filteredData);
      } catch (error) {
        console.error("Error fetching total players by game:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [selectedDays]);

  return (
    <Card className="shadow-md relative dark:bg-[--header-color-dark]">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Top Games by Company</CardTitle>
            <CardDescription>Stacked Bar Chart of Total Gameplays by Company Grouping</CardDescription>
          </div>

          <Select
              value={String(selectedDays)}
              onValueChange={(value) => setSelectedDays(Number(value))}
          >
            <SelectTrigger className="w-32 border border-gray-300 rounded-lg px-2 py-1 text-sm dark:text-white dark:bg-[--header-color-dark]">
              <SelectValue placeholder="Select Days" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>

        </div>
      </CardHeader>

      <CardContent>
        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm z-10">
            <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500"></div>
          </div>
        )}

        <ChartContainer className="w-full h-full" config={{ margin: { top: 20, right: 20, bottom: 20, left: 20 } }}>
          <BarChart data={chartData} layout="vertical">
            <CartesianGrid horizontal={false} />
            <XAxis type="number" tickLine={false} axisLine={false} />
            <YAxis dataKey="company" type="category" tickLine={false} axisLine={false} width={120} />
            <ChartTooltip content={<ChartTooltipContent hideLabel />} />

            {brandNames.map((brandName, idx) => (
              <Bar key={brandName} dataKey={brandName} stackId="a" fill={`hsl(${((idx + 50) * 40) % 360}, 70%, 50%)`} />
            ))}
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
